import { crearClienteHttp } from '@/api/cliente'
import type { Operador, PasoDeEntrada, SesionConsolaIniciada } from './tipos'

/**
 * La sesión de la consola del dueño. **Separada de la del portal** a
 * propósito (`docs/09-consola-del-dueno.md` §3 y §6).
 *
 * El testigo de la consola tiene otra audiencia y abre `/consola/*`; el del
 * portal abre `/interna/*`. El servidor ya los separa, pero si compartieran
 * almacén y lector aquí, entrar a una borraría la otra y un 401 de una sacaría
 * de las dos. Cada una tiene su clave, su lector y su cliente HTTP.
 *
 * ## Dónde vive el testigo
 *
 * En `sessionStorage`, bajo su propia clave, por lo mismo que el del portal
 * (`dominio/sesion/sesion.ts` lo explica entero): contra un XSS memoria y
 * `sessionStorage` valen lo mismo, y `sessionStorage` muere con la pestaña en
 * vez de quedarse al día siguiente en un equipo ajeno. Aquí esa última razón
 * pesa más que en el portal: este testigo abre la cuenta de **todos** los
 * contribuyentes.
 *
 * ## Qué NO guarda
 *
 * Ni la credencial, ni el testigo de paso, ni el secreto del segundo factor.
 * Los tres viven solo en el estado de la pantalla de entrada mientras dura, y
 * el de paso caduca a los cinco minutos en el servidor de todos modos.
 */

const CLAVE = 'ecf.consola.sesion'

export interface SesionConsola {
  testigo: string
  expira: Date
  operador: Operador
}

function recuperar(): SesionConsola | null {
  try {
    const crudo = sessionStorage.getItem(CLAVE)
    if (crudo === null) return null

    const guardada = JSON.parse(crudo) as Omit<SesionConsola, 'expira'> & { expira: string }
    const expira = new Date(guardada.expira)

    // Un testigo caducado se descarta aquí en vez de mandarse para que lo
    // rechacen: se vería la consola un instante y luego la entrada.
    if (Number.isNaN(expira.getTime()) || expira.getTime() <= Date.now()) {
      sessionStorage.removeItem(CLAVE)
      return null
    }

    return { ...guardada, expira }
  } catch {
    return null
  }
}

function guardar(sesion: SesionConsola | null): void {
  try {
    if (sesion === null) sessionStorage.removeItem(CLAVE)
    else sessionStorage.setItem(CLAVE, JSON.stringify(sesion))
  } catch {
    // Sin almacenamiento la sesión vive solo en memoria: recargar pide entrar otra vez.
  }
}

let actual: SesionConsola | null = recuperar()
let temporizador: ReturnType<typeof setTimeout> | null = null
const suscriptores = new Set<() => void>()

function avisar(): void {
  for (const s of suscriptores) s()
}

/**
 * Treinta minutos es la vida del testigo. Se cierra en el cliente al
 * cumplirse, sin esperar al 401: una consola abierta en una pestaña olvidada
 * tiene que volver a la entrada sola, no quedarse enseñando datos de todos los
 * contribuyentes hasta que alguien pulse algo.
 */
function programarCaducidad(): void {
  if (temporizador !== null) clearTimeout(temporizador)
  temporizador = null
  if (actual === null) return
  const restante = actual.expira.getTime() - Date.now()
  // `setTimeout` desborda por encima de ~24,8 días; aquí son minutos, pero se acota igual.
  temporizador = setTimeout(cerrarSesionConsola, Math.max(0, Math.min(restante, 2_147_000_000)))
}

programarCaducidad()

export function suscribirseConsola(alCambiar: () => void): () => void {
  suscriptores.add(alCambiar)
  return () => suscriptores.delete(alCambiar)
}

export function sesionConsolaActual(): SesionConsola | null {
  return actual
}

export function cerrarSesionConsola(): void {
  actual = null
  guardar(null)
  programarCaducidad()
  avisar()
}

/**
 * El cliente HTTP de la consola: el mismo transporte que el portal, con su
 * prefijo y su testigo. Un 401 cierra **esta** sesión y no la del portal.
 */
export const clienteConsola = crearClienteHttp({
  base: '/consola',
  leerTestigo: () => actual?.testigo ?? null,
  alCaducarSesion: cerrarSesionConsola,
})

/**
 * Primer paso: correo y credencial. El servidor contesta con el paso que toca
 * —enrolar el segundo factor o pedir el código— y un testigo de paso que solo
 * sirve para el segundo.
 */
export function pedirPaso(email: string, credencial: string): Promise<PasoDeEntrada> {
  return clienteConsola.crear<PasoDeEntrada>('/consola/sesion', { cuerpo: { email, credencial } })
}

/** Segundo paso: el código de seis dígitos. Si el paso era enrolar, lo confirma. */
export async function confirmarCodigo(testigoPaso: string, codigo: string): Promise<SesionConsola> {
  const respuesta = await clienteConsola.crear<SesionConsolaIniciada>('/consola/sesion/codigo', {
    cuerpo: { testigoPaso, codigo },
  })

  actual = {
    testigo: respuesta.testigo,
    expira: new Date(respuesta.expira),
    operador: respuesta.operador,
  }
  guardar(actual)
  programarCaducidad()
  avisar()
  return actual
}

export function esDueno(sesion: SesionConsola | null): boolean {
  return sesion?.operador.rol === 'dueno'
}
